import { Link } from 'react-router-dom'
import { ArrowLeft, Code, Book, Users, MessageCircle, Github, ExternalLink, Zap, Shield, Globe } from 'lucide-react'
import { motion } from 'framer-motion'

export default function DeveloperCommunity() {
  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  }

  const fadeInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.6, delay: 0.2 }
    }
  }

  const fadeInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.6, delay: 0.4 }
    }
  }

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  }

  return (
    <motion.div 
      className="min-h-screen bg-gray-900"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Back Navigation */}
        <motion.div className="mb-8" variants={fadeInUp}>
          <Link 
            to="/" 
            className="inline-flex items-center space-x-2 text-orange-400 transition-colors hover:text-orange-300"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div className="mb-12 text-center" variants={fadeInUp}>
          <div className="flex items-center justify-center mb-4">
            <Code className="w-8 h-8 text-orange-400 mr-3" />
            <h1 className="text-4xl font-bold text-white">Developer Community</h1>
          </div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Join thousands of developers building the future of card-to-crypto payments with PexiBlock's powerful APIs and tools
          </p>
        </motion.div>

        {/* Stats Section */}
        <motion.div className="mb-16" variants={fadeInUp}>
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            <div className="text-center p-6 bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50">
              <div className="text-3xl font-bold text-orange-400 mb-2">10k+</div>
              <div className="text-gray-300 text-sm">Active Developers</div>
            </div>
            <div className="text-center p-6 bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50">
              <div className="text-3xl font-bold text-blue-400 mb-2">500+</div>
              <div className="text-gray-300 text-sm">Integrated Apps</div>
            </div>
            <div className="text-center p-6 bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50">
              <div className="text-3xl font-bold text-green-400 mb-2">24/7</div>
              <div className="text-gray-300 text-sm">API Uptime</div>
            </div>
            <div className="text-center p-6 bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50">
              <div className="text-3xl font-bold text-purple-400 mb-2">50ms</div>
              <div className="text-gray-300 text-sm">Avg Response</div>
            </div>
          </div>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid gap-12 lg:grid-cols-2">
          
          {/* Quick Start Section */}
          <motion.div variants={fadeInLeft}>
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50">
              <div className="flex items-center mb-6">
                <Zap className="w-6 h-6 text-orange-400 mr-3" />
                <h2 className="text-2xl font-semibold text-white">Quick Start</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-white mb-3">Get Started in Minutes</h3>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-orange-500 text-white text-sm rounded-full flex items-center justify-center mt-0.5">1</div>
                      <div>
                        <p className="text-white font-medium">Sign up for API access</p>
                        <p className="text-gray-400 text-sm">Create your developer account and get instant API keys</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-orange-500 text-white text-sm rounded-full flex items-center justify-center mt-0.5">2</div>
                      <div>
                        <p className="text-white font-medium">Install our SDK</p>
                        <p className="text-gray-400 text-sm">Available for Node.js, Python, PHP, Ruby, and more</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-orange-500 text-white text-sm rounded-full flex items-center justify-center mt-0.5">3</div>
                      <div>
                        <p className="text-white font-medium">Make your first API call</p>
                        <p className="text-gray-400 text-sm">Process your first card-to-crypto payment</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-700">
                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <div className="text-xs text-gray-400 mb-2">Sample API Call</div>
                    <code className="text-green-400 text-sm">
                      {`curl -X POST https://api.pexiblock.com/v1/payments \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"amount": 100, "currency": "USD", "crypto": "BTC"}'`}
                    </code>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button className="flex-1 px-4 py-2 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 transition-colors">
                    Get API Keys
                  </button>
                  <button className="flex-1 px-4 py-2 bg-slate-700 text-white font-medium rounded-lg hover:bg-slate-600 transition-colors">
                    View Docs
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Resources Section */}
          <motion.div variants={fadeInRight}>
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50">
              <div className="flex items-center mb-6">
                <Book className="w-6 h-6 text-blue-400 mr-3" />
                <h2 className="text-2xl font-semibold text-white">Developer Resources</h2>
              </div>
              
              <div className="space-y-4">
                <a href="#" className="block p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors group">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-white group-hover:text-orange-400 transition-colors">API Documentation</h3>
                      <p className="text-gray-400 text-sm">Complete reference with examples and tutorials</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-orange-400" />
                  </div>
                </a>
                
                <a href="#" className="block p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors group">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-white group-hover:text-orange-400 transition-colors">SDK Libraries</h3>
                      <p className="text-gray-400 text-sm">Official libraries for popular programming languages</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-orange-400" />
                  </div>
                </a>
                
                <a href="#" className="block p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors group">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-white group-hover:text-orange-400 transition-colors">Code Examples</h3>
                      <p className="text-gray-400 text-sm">Ready-to-use code snippets and integrations</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-orange-400" />
                  </div>
                </a>
                
                <a href="#" className="block p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors group">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-white group-hover:text-orange-400 transition-colors">Webhooks Guide</h3>
                      <p className="text-gray-400 text-sm">Real-time notifications for payment events</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-orange-400" />
                  </div>
                </a>
                
                <a href="#" className="block p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors group">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-white group-hover:text-orange-400 transition-colors">Testing Guide</h3>
                      <p className="text-gray-400 text-sm">Sandbox environment and test scenarios</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-orange-400" />
                  </div>
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Community Channels */}
        <motion.div className="mt-16" variants={fadeInUp}>
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-4">Join Our Community</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Connect with fellow developers, get help with integration, and stay updated on the latest features
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 text-center">
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Discord Server</h3>
              <p className="text-gray-400 text-sm mb-4">
                Real-time chat with developers, support team, and community members
              </p>
              <button className="w-full px-4 py-2 bg-purple-500 text-white font-medium rounded-lg hover:bg-purple-600 transition-colors">
                Join Discord
              </button>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 text-center">
              <div className="w-12 h-12 bg-gray-500/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Github className="w-6 h-6 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">GitHub</h3>
              <p className="text-gray-400 text-sm mb-4">
                Open-source SDKs, examples, and contribute to our repositories
              </p>
              <button className="w-full px-4 py-2 bg-gray-700 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors">
                View on GitHub
              </button>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 text-center">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Developer Forum</h3>
              <p className="text-gray-400 text-sm mb-4">
                Ask questions, share solutions, and discuss best practices
              </p>
              <button className="w-full px-4 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition-colors">
                Visit Forum
              </button>
            </div>
          </div>
        </motion.div>

        {/* Features Section */}
        <motion.div className="mt-16" variants={fadeInUp}>
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Why Developers Choose PexiBlock</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Built by developers, for developers. Our API is designed to be simple, powerful, and reliable.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
              <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-5 h-5 text-orange-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-3">Lightning Fast</h3>
              <p className="text-gray-400 text-sm">
                Sub-50ms API response times with 99.99% uptime SLA. Built on modern infrastructure for maximum performance.
              </p>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-5 h-5 text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-3">Enterprise Security</h3>
              <p className="text-gray-400 text-sm">
                PCI DSS Level 1 compliant with end-to-end encryption, secure key management, and regular security audits.
              </p>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                <Globe className="w-5 h-5 text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-3">Global Coverage</h3>
              <p className="text-gray-400 text-sm">
                Process payments from customers worldwide with support for 150+ countries and multiple currencies.
              </p>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4">
                <Code className="w-5 h-5 text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-3">Developer First</h3>
              <p className="text-gray-400 text-sm">
                RESTful APIs, comprehensive SDKs, detailed documentation, and extensive code examples for every use case.
              </p>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center mb-4">
                <MessageCircle className="w-5 h-5 text-yellow-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-3">24/7 Support</h3>
              <p className="text-gray-400 text-sm">
                Dedicated developer support team available around the clock to help with integration and troubleshooting.
              </p>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-3">Active Community</h3>
              <p className="text-gray-400 text-sm">
                Join thousands of developers sharing knowledge, solutions, and building amazing crypto payment experiences.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div 
          className="mt-16 p-8 bg-gradient-to-r from-orange-500/10 to-blue-500/10 rounded-2xl border border-orange-500/20 text-center"
          variants={fadeInUp}
        >
          <h2 className="text-2xl font-bold text-white mb-4">Ready to Start Building?</h2>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Join the PexiBlock developer community today and start accepting card-to-crypto payments in your applications
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors">
              Get Started Free
            </button>
            <Link 
              to="/contact"
              className="px-8 py-3 bg-slate-700 text-white font-semibold rounded-lg hover:bg-slate-600 transition-colors"
            >
              Contact Sales
            </Link>
          </div>
        </motion.div>
      </div>
    </motion.div>
  )
}